import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Badge, Spinner2 } from 'components';
import { Api, handleSubscriptionError, handleSubscriptionSuccess } from 'lib';

/**
 * Component to toggle billing interval (monthly <-> yearly) for the same plan
 * @param {Object} currentSubscription - Current subscription object
 * @param {Function} onIntervalChange - Callback when interval changes successfully
 */
const IntervalToggle = ({ currentSubscription, onIntervalChange }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [isChanging, setIsChanging] = useState(false);
    
    const currentInterval = currentSubscription?.interval || 'monthly';
    const canToggle = currentSubscription && 
                     (currentSubscription.status === 'active' || 
                      currentSubscription.status === 'success');

    const handleToggle = async (newInterval) => {
        if (newInterval === currentInterval || !canToggle) {
            return;
        }

        if (!currentSubscription) {
            dispatch({
                type: 'HANDLE_RES',
                payload: { err: 'No active subscription' }
            });
            return;
        }

        setIsChanging(true);

        try {
            const res = await Api.post('/subscriptionPlans/stripe/change', {
                teamId: currentSubscription.teamId,
                newPlanId: currentSubscription.planId,
                interval: newInterval
            });

            const wasSuccess = handleSubscriptionSuccess(res, dispatch, () => {}, {
                successMessage: t('billing.intervalChanged', { 
                    interval: newInterval,
                    defaultValue: `Billing interval changed to ${newInterval}` 
                }),
                redirectUrl: null // Don't redirect for interval changes
            });

            if (wasSuccess && onIntervalChange) {
                onIntervalChange(newInterval);
            } else if (!wasSuccess) {
                handleSubscriptionError(res, dispatch, () => {}, {
                    defaultMessage: 'Failed to change interval'
                });
            }
        } catch (error) {
            console.error('Failed to change billing interval:', error);
            handleSubscriptionError(error, dispatch, () => {}, {
                defaultMessage: 'Failed to change billing interval'
            });
        } finally {
            setIsChanging(false);
        }
    };

    if (!currentSubscription) {
        return null;
    }

    return (
        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
            <button
                onClick={() => handleToggle('monthly')}
                disabled={isChanging || !canToggle}
                className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${
                    currentInterval === 'monthly'
                        ? 'bg-black text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
                {t('subscription.monthly', { defaultValue: 'Monthly' })}
            </button>

            <button
                onClick={() => handleToggle('yearly')}
                disabled={isChanging || !canToggle}
                className={`px-4 py-2 rounded-md font-medium text-sm transition-colors flex items-center gap-2 ${
                    currentInterval === 'yearly'
                        ? 'bg-black text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
                {t('subscription.yearly', { defaultValue: 'Yearly' })}
                {currentInterval !== 'yearly' && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">
                        {t('subscription.save20', { defaultValue: 'Save 20%' })}
                    </span>
                )}
            </button>

            {isChanging && (
                <div className="ml-2">
                    <Spinner2 col="#000" />
                </div>
            )}
        </div>
    );
};

export default IntervalToggle;
